'use client'

import { Button } from '@/components/ui/common/Button'
import { default as useAuthStore, default as useUserStore } from '@/store/userStore'
import { cn } from '@/utils'
import {
  Award,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  LogOut,
  Settings,
  Stethoscope,
  User,
  Users
} from 'lucide-react'
import { ReactNode, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

interface DashboardLayoutProps {
  children: ReactNode
}

const menuItems = [
  {
    title: 'Klasy 7-8',
    icon: ClipboardList,
    href: '/',
    submenu: [
      { title: 'Zrób test', href: '/' },
      { title: 'Moje testy', href: '/results' },
      { title: 'Mój rozwój', href: '/my-development', isMockup: true }
    ]
  },
  {
    title: 'Maturzyści',
    icon: GraduationCap,
    href: '/high-school-graduates',
    isMockup: true
  },
  {
    title: 'Studenci i kariera',
    icon: Users,
    href: '/students',
    isMockup: true
  },
  {
    title: 'Eksperci',
    icon: Stethoscope,
    href: '/experts',
    isMockup: true
  },
  {
    title: 'HR / Pracownicy',
    icon: Award,
    href: '/hr',
    isMockup: true
  }
]

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation()
  const { logout } = useUserStore()
  const { user } = useAuthStore()
  const initOpenSubmenu = menuItems[0].submenu?.some((a) => location.pathname == a.href)
    ? menuItems[0].href
    : null
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(initOpenSubmenu)
  const navigate = useNavigate()
  const isMockup =
    menuItems.find(
      (e) =>
        !e.submenu && location.pathname.replace('/', '').includes(e.href.replace('/', ''))
    )?.isMockup || location.pathname === '/my-development'

  return (
    <div className="flex min-h-screen w-full">
      {/* Menu boczne */}
      <div className="fixed top-0 left-0 flex h-screen w-64 flex-col bg-[#1d475f]">
        {/* Logo */}
        <div className="flex items-center justify-center p-6">
          <img
            alt="Skillpass Logo"
            className="h-auto w-full"
            src="/skillpassV2Transparent.svg"
          />
        </div>

        {/* Menu */}
        <nav className="flex-1 px-4 py-2">
          {menuItems.map((item) => (
            <div key={item.href}>
              {item.submenu ? (
                <div className={openSubmenu === item.href ? 'pb-1' : ''}>
                  <button
                    className={cn(
                      'mb-1 flex w-full items-center justify-between gap-3 rounded-lg px-4 py-3 transition-colors',
                      openSubmenu === item.href && openSubmenu !== menuItems[0].href
                        ? 'bg-white/10 text-white'
                        : 'text-white/70 hover:bg-white/5 hover:text-white'
                    )}
                    onClick={() =>
                      setOpenSubmenu(openSubmenu === item.href ? null : item.href)
                    }>
                    <div className="flex items-center gap-3">
                      <item.icon className="h-5 w-5 text-[#2bcaca]" />
                      <span>{item.title}</span>
                    </div>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 transition-transform',
                        openSubmenu === item.href ? 'rotate-180' : ''
                      )}
                    />
                  </button>
                  {openSubmenu === item.href && (
                    <div className="mt-1 ml-8 space-y-1">
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.href}
                          className={cn(
                            'block rounded-lg px-4 py-2 text-sm transition-colors',
                            location.pathname === subItem.href
                              ? 'bg-white/10 text-white'
                              : 'text-white/70 hover:bg-white/5 hover:text-white'
                          )}
                          to={subItem.href}>
                          {subItem.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  className={cn(
                    'mb-1 flex items-center gap-3 rounded-lg px-4 py-3 transition-colors',
                    location.pathname === item.href
                      ? 'bg-white/10 text-white'
                      : 'text-white/70 hover:bg-white/5 hover:text-white'
                  )}
                  to={item.href}>
                  <item.icon className="h-5 w-5 text-[#2bcaca]" />
                  <span>{item.title}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Przycisk wylogowania */}
        <div className="p-4">
          <Button
            className="w-full justify-start gap-3 border-white/20 text-white/70 hover:bg-white/5 hover:text-white"
            onClick={logout}
            variant="outline">
            <LogOut className="h-5 w-5 text-[#2bcaca]" />
            <span>Wyloguj się</span>
          </Button>
        </div>
      </div>

      {/* Główna zawartość */}
      <div className="ml-64 flex flex-1 flex-col bg-gray-50">
        {/* Górna belka */}
        <div className="fixed top-0 right-0 left-64 z-40 w-[calc(100%-16rem)]">
          <div className="flex items-center justify-between border-b border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-lg font-medium text-gray-900" />

            {/* Prawa strona z avatarem i ustawieniami */}
            <div className="flex items-center">
              <div className="flex cursor-pointer items-center gap-2">
                <div className="mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-700">
                  {user ? `${user.firstName} ${user.lastName}` : 'Użytkownik'}
                </span>
                <ChevronRight className="h-4 w-4 rotate-90 text-gray-400" />
              </div>
              {/* Ikonka ustawień */}
              <div
                className="ml-4 cursor-pointer text-gray-500 transition-colors hover:text-gray-700"
                onClick={() => {
                  navigate('/user-profile-settings')
                }}>
                <Settings className="animate-spin-slow h-5 w-5" />
              </div>
            </div>
          </div>
          {/* Mockup Banner */}
          {isMockup && (
            <div className="right-0 left-64 z-30 border-b-2 border-amber-200 bg-amber-50 px-6 py-3">
              <div className="flex items-center justify-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 animate-pulse rounded-full bg-amber-400"></div>
                  <span className="font-semibold text-amber-800">MOCKUP</span>
                </div>
                <span className="text-amber-700">
                  Ta sekcja jest w fazie projektowania - funkcjonalności są jedynie
                  demonstracyjne
                </span>
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-amber-800">DEMO</span>
                  <div className="h-3 w-3 animate-pulse rounded-full bg-amber-400"></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Główna zawartość */}
        <main
          className={`w-full flex-1 overflow-y-auto p-8 ${isMockup ? 'mt-[105px]' : 'mt-[57px]'}`}>
          {children}
        </main>
      </div>
    </div>
  )
}
